import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Switch,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { SCHEDULE_KEY, WEEKDAYS, scheduleApi, type Board, type Member, type Weekday, type WorkHours } from './schedule-api';

const DEFAULT_HOURS: WorkHours = {
  0: { start: '08:00', end: '17:00' },
  1: { start: '08:00', end: '17:00' },
  2: { start: '08:00', end: '17:00' },
  3: { start: '08:00', end: '17:00' },
  4: { start: '08:00', end: '17:00' },
  5: { start: '08:00', end: '13:00' },
  6: null,
};
const DAYS: Weekday[] = [0, 1, 2, 3, 4, 5, 6];

/**
 * שעות העבודה וימי החופש של עובד. השיבוץ האוטומטי מדלג על מי שלא זמין,
 * והלוח מציג אזהרה כשמשבצים ידנית מחוץ לזמינות.
 */
export function MemberAvailabilityDialog({
  member,
  timeOff,
  onClose,
}: {
  member: Member;
  timeOff: Board['timeOff'];
  onClose: () => void;
}) {
  const { t } = useTranslation(['schedule', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const [hours, setHours] = useState<WorkHours>(member.workHours ?? DEFAULT_HOURS);
  const [off, setOff] = useState({ startDate: '', endDate: '', reason: '' });
  const mine = timeOff.filter((off) => off.userId === member.id);

  const refresh = () => qc.invalidateQueries({ queryKey: SCHEDULE_KEY });
  const saveHours = useMutation({
    mutationFn: () => scheduleApi.setWorkHours(member.id, hours),
    onSuccess: async () => {
      toast.success(t('availability.hoursSaved'));
      await refresh();
      onClose();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });
  const addOff = useMutation({
    mutationFn: () =>
      scheduleApi.addTimeOff({
        userId: member.id,
        startDate: off.startDate,
        endDate: off.endDate || off.startDate,
        ...(off.reason.trim() && { reason: off.reason.trim() }),
      }),
    onSuccess: async () => {
      setOff({ startDate: '', endDate: '', reason: '' });
      await refresh();
    },
    onError: (e) => toast.error(t('availability.addFailed'), e instanceof ApiError ? e.message : undefined),
  });
  const removeOff = useMutation({
    mutationFn: (id: string) => scheduleApi.removeTimeOff(id),
    onSuccess: () => void refresh(),
  });

  const setDay = (d: Weekday, patch: { start?: string; end?: string } | null) =>
    setHours((h) => ({ ...h, [d]: patch === null ? null : { ...(h[d] ?? { start: '08:00', end: '17:00' }), ...patch } }));
  const invalid = DAYS.some((d) => {
    const v = hours[d];
    return v ? v.end <= v.start : false;
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="availability-desc">
        <DialogHeader>
          <DialogTitle>{t('availability.title', { name: member.name })}</DialogTitle>
          <DialogDescription id="availability-desc">
            {t('availability.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <section>
            <h3 className="mb-2 text-sm font-medium text-fg">{t('availability.workHours')}</h3>
            <ul className="space-y-1.5">
              {DAYS.map((d) => {
                const v = hours[d];
                return (
                  <li key={d} className="flex items-center gap-3">
                    <Switch
                      checked={!!v}
                      onCheckedChange={(on) => setDay(d, on ? {} : null)}
                      aria-label={t('availability.worksOn', { day: WEEKDAYS[d] })}
                    />
                    <span className="w-24 text-sm text-fg">{WEEKDAYS[d]}</span>
                    {v ? (
                      <>
                        <Input
                          type="time"
                          dir="ltr"
                          className="h-9 w-28"
                          aria-label={t('availability.startOn', { day: WEEKDAYS[d] })}
                          value={v.start}
                          onChange={(e) => setDay(d, { start: e.target.value })}
                        />
                        <span className="text-xs text-fg-subtle">{t('availability.until')}</span>
                        <Input
                          type="time"
                          dir="ltr"
                          className="h-9 w-28"
                          aria-label={t('availability.endOn', { day: WEEKDAYS[d] })}
                          value={v.end}
                          onChange={(e) => setDay(d, { end: e.target.value })}
                        />
                      </>
                    ) : (
                      <span className="text-xs text-fg-subtle">{t('availability.notWorking')}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-medium text-fg">{t('availability.timeOff')}</h3>
            {mine.length ? (
              <ul className="mb-3 space-y-1">
                {mine.map((item) => (
                  <li key={item.id} className="flex items-center justify-between rounded-lg bg-surface-sunken px-3 py-1.5 text-sm">
                    <span>
                      {formatDate(item.startDate)}
                      {item.endDate !== item.startDate ? ` – ${formatDate(item.endDate)}` : ''}
                      {item.reason ? <span className="text-fg-muted"> · {item.reason}</span> : null}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('common:actions.remove')}
                      loading={removeOff.isPending && removeOff.variables === item.id}
                      onClick={() => removeOff.mutate(item.id)}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-3 text-xs text-fg-muted">{t('availability.noTimeOff')}</p>
            )}
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 text-2xs text-fg-muted">
                {t('availability.fromDate')}
                <Input type="date" dir="ltr" className="h-9 w-36" value={off.startDate} onChange={(e) => setOff({ ...off, startDate: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1 text-2xs text-fg-muted">
                {t('availability.toDate')}
                <Input type="date" dir="ltr" className="h-9 w-36" value={off.endDate} min={off.startDate} onChange={(e) => setOff({ ...off, endDate: e.target.value })} />
              </label>
              <label className="flex min-w-32 flex-1 flex-col gap-1 text-2xs text-fg-muted">
                {t('availability.reason')}
                <Input className="h-9" maxLength={120} value={off.reason} onChange={(e) => setOff({ ...off, reason: e.target.value })} />
              </label>
              <Button size="sm" variant="secondary" disabled={!off.startDate} loading={addOff.isPending} onClick={() => addOff.mutate()}>
                <Plus aria-hidden />
                {t('common:actions.add')}
              </Button>
            </div>
          </section>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
          <Button disabled={invalid} loading={saveHours.isPending} onClick={() => saveHours.mutate()}>
            {t('availability.saveHours')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
